import { useQuery } from "@apollo/client";
import { Button, Loading } from "@landtechnologies/components";
import classNames from "classnames";
import { useEffect, useState } from "react";
import {
  LandTechEndpoints,
  Stores,
  formatToCurrency,
  routedClientWithoutTypenames,
  useGlobalState,
  useTranslation,
} from "react-migration/lib";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";
import ACCOUNT from "react-migration/domains/sites/apollo/sites-api/queries/account.gql";
import { AccountResponseData } from "react-migration/domains/sites/typings";

export const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [{ locale }] = useGlobalState(Stores.user);
  const [currentBalance, setCurrentBalance] = useState<number | null>(null);

  const {
    loading,
    data: accountData,
    error,
  } = useQuery<AccountResponseData>(ACCOUNT, {
    client: routedClientWithoutTypenames,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!loading && accountData && accountData.account.balance >= 0) {
      const balanceInPounds = accountData.account.balance / 100;
      setCurrentBalance(balanceInPounds);
    }
  }, [loading, accountData]);

  const textClassNames = [
    "atlas-flex",
    "atlas-justify-center",
    "atlas-text-sm",
    "atlas-text-content-primary",
  ];
  const paymentSummaryRowClassNames = [
    "atlas-flex",
    "atlas-flex-row",
    "atlas-justify-between",
    "atlas-border-b",
    "atlas-border-border-divider",
    "atlas-py-3",
    "atlas-text-sm",
  ];

  return (
    <div data-testid="utility-report-payment-success" className="atlas-p-5">
      <div className="atlas-flex atlas-flex-col atlas-gap-y-2 atlas-py-5">
        <i className="icon-lt-checkbox-circle-line atlas-text-4xl atlas-text-background-action-hover atlas-flex atlas-justify-center" />
        <span className={classNames("atlas-font-semibold", textClassNames)}>
          {t("sites.utility_report.confirmed")}
        </span>
        <span className={classNames(textClassNames)}>
          {t("sites.utility_report.it_will_take_up_to_10_days_to_create_your_report")}
        </span>
        <span className={classNames(textClassNames)}>
          {t("sites.utility_report.we_will_send_you_a_notification_when_it_is_ready")}
        </span>
      </div>
      <div className={classNames(paymentSummaryRowClassNames, "atlas-font-medium")}>
        <span>{t("sites.utility_report.subtotal")}</span>
        <span>{formatToCurrency({ amount: 600, currency: "GBP", locale })}</span>
      </div>
      <div className={classNames(paymentSummaryRowClassNames)}>
        <span>{t("sites.utility_report.vat_20")}</span>
        <span>{formatToCurrency({ amount: 120, currency: "GBP", locale })}</span>
      </div>
      <div
        className={classNames(paymentSummaryRowClassNames, "atlas-font-bold", "atlas-border-none")}
      >
        <span>{t("sites.utility_report.total")}</span>
        <span>{formatToCurrency({ amount: 720, currency: "GBP", locale })}</span>
      </div>
      {loading && (
        <div className="atlas-mt-16">
          <Loading />
        </div>
      )}
      {!loading && !error && (
        <div className="atlas-mt-14">
          <span className="atlas-text-sm atlas-font-semibold">
            {t("sites.utility_report.your_current_LandTech_balance_is", {
              currentBalance: (
                <span className="atlas-text-base atlas-block atlas-w-full atlas-pt-0.5">
                  {formatToCurrency({ amount: currentBalance ?? 0, currency: "GBP", locale })}
                </span>
              ),
            })}
          </span>
          <Button.Secondary
            size="small"
            className="atlas-mt-2.5 atlas-border-0"
            onClick={() => router.push({ name: ROUTES.DASHBOARD_TOPUP })}
          >
            {t("sites.utility_report.top_up_credits")}
          </Button.Secondary>
        </div>
      )}
    </div>
  );
};
